import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import useApi from '../../api/hooks/useApi';

// 전역 캐시 (CommonFilterSection과 공유)
const globalCache = {
    threads: null,
    loading: {
        threads: false
    }
};

const ItemSearchDialog = ({ open, onClose, onSelect, initialValue = '' }) => {
    const { t } = useTranslation();
    const api = useApi();
    const [searchTerm, setSearchTerm] = useState(initialValue);
    const [threads, setThreads] = useState([]);
    const [filteredThreads, setFilteredThreads] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // Thread 데이터 가져오기
    const fetchThreads = async () => {
        if (globalCache.threads || globalCache.loading.threads) return; // 전역 캐시 확인
        
        globalCache.loading.threads = true;
        setIsLoading(true);
        setError('');
        try {
            console.log('fetchThreads');
            const response = await api.get('/info/v2/thread/list');
            if (response.data) {
                globalCache.threads = response.data;
                setThreads(response.data);
                setFilteredThreads(response.data);
            }
        } catch (error) {
            console.error('Thread 데이터 가져오기 실패:', error);
            setError(t('error.fetch'));
            // 임시 데이터 (실제 구현 시 제거)
            const tempThreads = [
                { 
                    thread_id: 100411, 
                    thread_name: 'SPUN POLY 40/2(TEX27)', 
                    specification: 'SEMIDAL',
                    weight: 1.4175,
                    unit: 'EA',
                    yarn_type: 'SPUN POLY',
                    creator_region: 'indonesia',
                    created_by: 'test4454',
                    created_at: '2025-03-04T10:48:55.161295'
                },
                { 
                    thread_id: 100412, 
                    thread_name: 'SPUN POLY 40/2(TEX27)', 
                    specification: 'BRIGHT',
                    weight: 1.25,
                    unit: 'EA',
                    yarn_type: 'SPUN POLY',
                    creator_region: 'indonesia',
                    created_by: 'test4454',
                    created_at: '2025-03-04T10:49:37.843374'
                },
                { 
                    thread_id: 100413, 
                    thread_name: 'SPUN POLY 60/3(TEX27)', 
                    specification: 'BRIGHT',
                    weight: 1.25,
                    unit: 'EA',
                    yarn_type: 'SPUN POLY',
                    creator_region: 'indonesia',
                    created_by: 'test4454',
                    created_at: '2025-03-04T10:51:26.424579'
                }
            ];
            globalCache.threads = tempThreads;
            setThreads(tempThreads);
            setFilteredThreads(tempThreads);
        } finally {
            globalCache.loading.threads = false;
            setIsLoading(false);
        }
    };

    // 다이얼로그 열릴 때 데이터 로드 및 초기 검색어 적용
    useEffect(() => {
        if (open) {
            // 전역 캐시에서 데이터가 있으면 먼저 설정
            if (globalCache.threads) {
                setThreads(globalCache.threads);
                setFilteredThreads(globalCache.threads);
            } else if (!globalCache.loading.threads) {
                // 캐시에 데이터가 없고 로딩 중이 아니면 API 호출
                fetchThreads();
            }
            // 초기 검색어가 있다면 자동으로 필터링 적용
            if (initialValue && initialValue.trim() !== '') {
                setSearchTerm(initialValue);
            }
        }
    }, [open, initialValue]);

    // 검색어 변경 시 필터링
    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredThreads(threads);
            return;
        }

        const filtered = threads.filter(thread => 
            thread.thread_id.toString().includes(searchTerm) ||
            thread.thread_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            thread.specification.toLowerCase().includes(searchTerm.toLowerCase()) ||
            thread.yarn_type.toLowerCase().includes(searchTerm.toLowerCase())
        );
        setFilteredThreads(filtered);
    }, [searchTerm, threads]);

    // Thread 선택 처리
    const handleThreadSelect = (thread) => {
        onSelect(thread);
        onClose();
    };

    // 검색어 초기화
    const handleClose = () => {
        setSearchTerm('');
        setError('');
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle>
                {t('common.searchFields.item')} {t('common.search')}
            </DialogTitle>
            <DialogContent>
                <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t('common.searchFields.item') + ' ' + t('common.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 2, mt: 1 }}
                    autoFocus
                />

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {isLoading ? (
                    <Box display="flex" justifyContent="center" p={3}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {filteredThreads.length === 0 ? (
                            <ListItem>
                                <ListItemText 
                                    primary={t('common.noResults')} 
                                    sx={{ textAlign: 'center' }}
                                />
                            </ListItem>
                        ) : (
                            filteredThreads.map((thread, index) => (
                                <React.Fragment key={thread.thread_id}>
                                    <ListItem disablePadding>
                                        <ListItemButton onClick={() => handleThreadSelect(thread)}>
                                            <ListItemText
                                                primary={
                                                    <Box>
                                                        <Typography variant="subtitle1" fontWeight="bold">
                                                            {thread.thread_name}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            ID: {thread.thread_id} | {thread.specification} | {thread.yarn_type}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Weight: {thread.weight} {thread.unit}
                                                        </Typography>
                                                    </Box>
                                                }
                                            />
                                        </ListItemButton>
                                    </ListItem>
                                    {index < filteredThreads.length - 1 && <Divider />}
                                </React.Fragment>
                            ))
                        )}
                    </List>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default ItemSearchDialog;
